// client/src/pages/ProjectDetail.tsx
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Project, ProjectCategoryLabels } from '../shared/models/Project';
import './ProjectDetail.css';

const ProjectDetail: React.FC = () => {
    const { slug } = useParams<{ slug: string }>();
    const [project, setProject] = useState<Project | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchProject = async () => {
            if (!slug) {
                setError('Project not found');
                setLoading(false);
                return;
            }

            try {
                const response = await fetch(`/.netlify/functions/admin-project?slug=${slug}`);
                
                if (!response.ok) {
                    if (response.status === 404) {
                        setError('Project not found');
                    } else {
                        throw new Error('Failed to fetch project');
                    }
                    setLoading(false);
                    return;
                }

                const data = await response.json();
                
                if (data.data.status !== 'published') {
                    setError('This project is not available');
                    setLoading(false);
                    return;
                }

                setProject(data.data);
                setError('');
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Failed to load project');
            } finally {
                setLoading(false);
            }
        };

        fetchProject();
    }, [slug]);

    if (loading) {
        return (
            <div className="project-detail-container">
                <div className="loading-spinner">Loading project...</div>
            </div>
        );
    }

    if (error || !project) {
        return (
            <div className="project-detail-container">
                <div className="error-container">
                    <h2>Project Not Found</h2>
                    <p>{error || 'The project you are looking for does not exist.'}</p>
                    <Link to="/projects" className="back-link">← Back to Projects</Link>
                </div>
            </div>
        );
    }

    return (
        <div className="project-detail-container">
            {/* Breadcrumb */}
            <nav className="breadcrumb">
                <Link to="/">Home</Link>
                <span className="separator">›</span>
                <Link to="/projects">Projects</Link>
                <span className="separator">›</span>
                <span className="breadcrumb-current">{project.title}</span>
            </nav>

            {/* Project Header */}
            <header className="project-header">
                <div className="project-meta-top">
                    <span className="project-category-badge">
                        {ProjectCategoryLabels[project.category]}
                    </span>
                    {project.featured && (
                        <span className="featured-badge">⭐ Featured</span>
                    )}
                    {project.isOngoing && (
                        <span className="ongoing-badge">🔄 Ongoing</span>
                    )}
                    <span className="project-impact">
                        Impact: {project.impact}
                    </span>
                </div>
                <h1>{project.title}</h1>
                {project.subtitle && (
                    <p className="project-subtitle">{project.subtitle}</p>
                )}
                <div className="project-dates">
                    <span>Started: {new Date(project.startDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })}</span>
                    {project.endDate && (
                        <span>Ended: {new Date(project.endDate).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                        })}</span>
                    )}
                </div>
            </header>

            {/* Project Body */}
            <div className="project-body">
                {/* Description */}
                <section className="project-description-section">
                    <h2>About This Project</h2>
                    <p className="project-full-description">{project.description}</p>
                    {project.fullDescription && (
                        <p className="project-full-description">{project.fullDescription}</p>
                    )}
                </section>

                {/* Approach */}
                {project.approach && (
                    <section className="project-approach-section">
                        <h2>Approach</h2>
                        <p>{project.approach}</p>
                    </section>
                )}

                {/* Objectives */}
                {project.objectives && project.objectives.length > 0 && (
                    <section className="project-objectives">
                        <h2>Objectives</h2>
                        <ul>
                            {project.objectives.map((objective, index) => (
                                <li key={index}>{objective}</li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Expected Outcomes */}
                {project.expectedOutcomes && project.expectedOutcomes.length > 0 && (
                    <section className="project-outcomes">
                        <h2>Expected Outcomes</h2>
                        <ul>
                            {project.expectedOutcomes.map((outcome, index) => (
                                <li key={index}>{outcome}</li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Impact Metrics */}
                {project.metrics && project.metrics.length > 0 && (
                    <section className="project-metrics">
                        <h2>Impact Metrics</h2>
                        <div className="metrics-grid">
                            {project.metrics.map((metric, index) => (
                                <div key={index} className="metric-card">
                                    <span className="metric-value">{metric.value}</span>
                                    <span className="metric-label">{metric.label}</span>
                                    {metric.description && (
                                        <span className="metric-description">{metric.description}</span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Partners */}
                {project.partners && project.partners.length > 0 && (
                    <section className="project-partners">
                        <h2>Partners</h2>
                        <div className="partners-list">
                            {project.partners.map((partner, index) => (
                                <div key={index} className="partner-item">
                                    <span className="partner-name">{partner.name}</span>
                                    <span className="partner-type">{partner.type}</span>
                                    {partner.url && (
                                        <a href={partner.url} target="_blank" rel="noopener noreferrer">
                                            Visit →
                                        </a>
                                    )}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                {/* Location */}
                {project.regions && project.regions.length > 0 && (
                    <section className="project-location">
                        <h2>Location</h2>
                        <p>
                            <strong>Regions:</strong> {project.regions.join(', ')}
                        </p>
                        {project.districts && project.districts.length > 0 && (
                            <p>
                                <strong>Districts:</strong> {project.districts.join(', ')}
                            </p>
                        )}
                    </section>
                )}

                {/* Tags */}
                {project.tags && project.tags.length > 0 && (
                    <section className="project-tags">
                        <h3>Tags</h3>
                        <div className="tags-list">
                            {project.tags.map((tag, index) => (
                                <span key={index} className="tag">#{tag}</span>
                            ))}
                        </div>
                    </section>
                )}

                {/* Back Button */}
                <div className="back-to-projects">
                    <Link to="/projects" className="back-link">← Back to Projects</Link>
                </div>
            </div>
        </div>
    );
};

export default ProjectDetail;